/**
 * Test veya Video görevleri için hedef URL'yi oluşturur.
 * Test tipi görevlerde `soru.bitmiyo.com.tr` subdomain bağlantıları kullanılır.
 */
export function getAssignmentUrl(type: 'video' | 'odev' | 'test', resourceRef: string | null | undefined): string | null {
  if (!resourceRef || !resourceRef.trim()) return null;
  const ref = resourceRef.trim();

  if (type === 'test') {
    if (ref.startsWith('http://') || ref.startsWith('https://')) {
      return ref;
    }
    // Sadece test paylaşım kodu veya ID'si girilmişse soru subdomain URL'ine dönüştür
    return `https://soru.bitmiyo.com.tr/coz/${ref}`;
  }

  if (type === 'video') {
    if (ref.startsWith('http://') || ref.startsWith('https://')) {
      return ref;
    }
    return `https://${ref}`;
  }

  if (type === 'odev') {
    if (ref.startsWith('http://') || ref.startsWith('https://')) {
      return ref;
    }
  }

  return null;
}

import type { AssignmentRow } from '../types/sinif';

export interface GroupedAssignment extends Omit<AssignmentRow, 'student_id'> {
  totalStudents: number;
  completedStudents: number;
  allAssignmentIds: string[];
  student_ids: string[];
}

/**
 * Öğretmen sınıf görünümünde aynı anda atanan veya aynı başlığa/zaman damgasına sahip
 * öğrenci ödevlerini tek bir ana ödev grubu altında birleştirir.
 */
export function groupAssignmentsForTeacher(assignments: AssignmentRow[]): GroupedAssignment[] {
  const groups: GroupedAssignment[] = [];

  for (const a of assignments) {
    const aTime = new Date(a.created_at).getTime();
    const existing = groups.find(g => {
      const gTime = new Date(g.created_at).getTime();
      const sameClass = g.class_id === a.class_id;
      const sameTitle = g.title.trim().toLowerCase() === a.title.trim().toLowerCase();
      const sameType = g.type === a.type;
      const closeTime = Math.abs(gTime - aTime) < 180000; // 3 dakikalık toplu atama penceresi
      return sameClass && sameTitle && sameType && closeTime;
    });

    if (existing) {
      existing.totalStudents += 1;
      existing.allAssignmentIds.push(a.id);
      existing.student_ids.push(a.student_id);
      if (a.status === 'tamamlandi') {
        existing.completedStudents += 1;
      }
      if (a.is_pinned) {
        existing.is_pinned = true;
      }
    } else {
      groups.push({
        id: a.id,
        teacher_id: a.teacher_id,
        class_id: a.class_id,
        type: a.type,
        title: a.title,
        description: a.description,
        resource_ref: a.resource_ref,
        due_date: a.due_date,
        status: a.status,
        completed_at: a.completed_at,
        is_pinned: Boolean(a.is_pinned),
        created_at: a.created_at,
        totalStudents: 1,
        completedStudents: a.status === 'tamamlandi' ? 1 : 0,
        allAssignmentIds: [a.id],
        student_ids: [a.student_id],
      });
    }
  }

  for (const g of groups) {
    if (g.completedStudents === g.totalStudents && g.totalStudents > 0) {
      g.status = 'tamamlandi';
    } else if (g.completedStudents > 0) {
      g.status = 'devam_ediyor';
    }
  }

  return groups;
}

/**
 * Son teslim tarihlerini Türk tarih formatına göre biçimlendirir (Örn: "15 Ağustos 2026, 23:59").
 */
export function formatTurkishDueDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Süresiz';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'Süresiz';
  const dateFormatted = d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${dateFormatted}, ${hours}:${minutes}`;
}

/**
 * Seçilen YYYY-MM-DD tarih dizesini saat 23:59:59 teslim süresiyle kaydetmek üzere biçimlendirir.
 */
export function formatDueDateForSave(dateOnlyStr: string | null | undefined): string | null {
  if (!dateOnlyStr || !dateOnlyStr.trim()) return null;
  const raw = dateOnlyStr.trim();
  if (raw.includes('T')) return raw;
  return `${raw}T23:59:59`;
}


